/**
 * <media-lightbox> — full-screen photo viewer for the product gallery
 * (markup: snippets/media-lightbox.liquid).
 *
 * Opens from its gallery (data-gallery): a tap / click on a photo, or a two-finger
 * pinch on one — the viewer opens mid-gesture and the zoom follows the fingers. The
 * photo grows out of its place in the gallery and shrinks back into it on close.
 *
 * Touch: swipe between photos (native scroll-snap); double tap cycles fit → fill the
 * screen → close-up; pinch 1–4× with a soft overshoot that springs back; pan with
 * momentum and rubber-band edges; pulled past the edge while zoomed, the next photo;
 * swipe down to close; a single tap hides / shows the controls.
 * Mouse: click cycles the same levels at the cursor, moving pans; ctrl + wheel zooms;
 * arrow keys and Esc.
 *
 * Lifecycle: every listener is registered with one AbortController and removed in
 * disconnectedCallback (the product section is replaced on colour swaps); window
 * pointer listeners exist only while fingers are down.
 */
if (!customElements.get('media-lightbox')) {
  const { reduceMotion, scrollToSlide, closeDialog, cancelClose, afterEvent } = window.themeUtils;

  const MAX_SCALE = 4;
  const RUBBER = 0.35; // share of the finger's travel applied beyond an edge
  const OVERZOOM_DAMPING = 0.2; // logarithmic stretch past MAX_SCALE
  const MIN_PINCH_SCALE = 0.6;
  const DOUBLE_TAP_MS = 280;
  const DOUBLE_TAP_SLOP = 40; // px between the two taps
  const MOVE_SLOP = 6; // px before a touch counts as a drag
  const DISMISS_DISTANCE = 110; // px pulled down to close
  const DISMISS_VELOCITY = 0.6; // px/ms flick down to close
  const EDGE_FLIP_DISTANCE = 60; // px pulled past the edge (zoomed) to change photo
  const MOMENTUM_MS = 220; // how far a flick carries, in ms of its velocity
  const ZOOM_MS = 300;
  const HERO_MS = 320;
  const CLOSE_FALLBACK_MS = 350;
  const SLIDE_WIDTHS = [1000, 1600, 2400];
  const SHARP_WIDTH = 2400;
  const THUMB_WIDTH = 160;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const sized = (src, width) => {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.href;
    } catch (e) {
      return src;
    }
  };

  // The rectangle a contained image actually paints inside its box.
  const contentRect = (box, ratio) => {
    let width = box.width;
    let height = width / (ratio || 1);
    if (height > box.height) {
      height = box.height;
      width = height * (ratio || 1);
    }
    return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
  };

  const ratioOf = (img) => Number(img?.dataset.ratio) || (img?.naturalWidth ? img.naturalWidth / img.naturalHeight : 1);

  class MediaLightbox extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.z = { s: 1, x: 0, y: 0 };
      this.pointers = new Map();
      this.galleryTouches = new Map();
    }

    connectedCallback() {
      this.gallery = document.getElementById(this.dataset.gallery);
      this.dialog = this.querySelector('dialog');
      if (!this.gallery || !this.dialog) return;
      this.track = this.dialog.querySelector('[data-mlb-track]');
      this.count = this.dialog.querySelector('[data-mlb-count]');
      this.thumbs = this.dialog.querySelector('[data-mlb-thumbs]');
      this.hint = this.dialog.querySelector('[data-mlb-hint]');
      const labels = this.dialog.querySelector('[data-mlb-labels]')?.content;
      this.labels = {
        slide: labels?.querySelector('[data-label="slide"]')?.textContent || '{n} / {total}',
        thumb: labels?.querySelector('[data-label="thumb"]')?.textContent || '{n}',
      };

      this.controller = new AbortController();
      const signal = this.controller.signal;
      // Capture phase on the gallery: runs before the gallery's own modal opener.
      this.gallery.addEventListener('click', (e) => this.onGalleryClick(e), { capture: true, signal });
      this.gallery.addEventListener('pointerdown', (e) => this.onGalleryPointerDown(e), { capture: true, signal });
      this.gallery.addEventListener('pointerup', (e) => this.galleryTouches.delete(e.pointerId), { capture: true, signal });
      this.gallery.addEventListener('pointercancel', (e) => this.galleryTouches.delete(e.pointerId), { capture: true, signal });

      this.dialog.addEventListener('cancel', (e) => this.onCancel(e), { signal });
      this.dialog.addEventListener('close', () => this.onClosed(), { signal });
      this.dialog.addEventListener('click', (e) => this.onDialogClick(e), { signal });
      this.dialog.addEventListener('keydown', (e) => this.onKeydown(e), { signal });
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
      this.track.addEventListener('mousemove', (e) => this.onMouseMove(e), { passive: true, signal });
      this.track.addEventListener('wheel', (e) => this.onWheel(e), { passive: false, signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unwatchPointers();
      this.stopAnimation();
      clearTimeout(this.singleTapTimer);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('mlb-open');
    }

    /* ================= gallery → viewer ================= */

    galleryItems() {
      return [...this.gallery.querySelectorAll('.product__media-list > .product__media-item')].filter(
        (item) => item.querySelector('.product__media img') && !item.querySelector('deferred-media, video, iframe, model-viewer')
      );
    }

    onGalleryClick(event) {
      const item = event.target.closest('.product__media-item');
      if (!item || event.button > 0 || event.metaKey || event.ctrlKey) return;
      const index = this.galleryItems().indexOf(item);
      if (index < 0) return;
      event.preventDefault();
      event.stopPropagation();
      if (!this.suppressClick) this.open(index);
    }

    // Two fingers on a gallery photo: open the viewer and hand the pinch over to it.
    onGalleryPointerDown(event) {
      if (event.pointerType !== 'touch' || this.dialog.open) return;
      const item = event.target.closest('.product__media-item');
      if (!item) return;
      this.galleryTouches.set(event.pointerId, { x: event.clientX, y: event.clientY, item });
      if (this.galleryTouches.size !== 2) return;
      const touches = [...this.galleryTouches.entries()];
      this.galleryTouches.clear();
      if (touches[0][1].item !== touches[1][1].item) return;
      const index = this.galleryItems().indexOf(item);
      if (index < 0) return;
      // The fingers lift after the viewer is open; their click must not reopen it.
      this.suppressClick = true;
      requestAnimationFrame(() => setTimeout(() => (this.suppressClick = false), DOUBLE_TAP_MS));
      this.open(index, { instant: true });
      touches.forEach(([id, p]) => this.pointers.set(id, { x: p.x, y: p.y, t: performance.now() }));
      this.watchPointers();
      this.startPinch();
    }

    /* ================= open / close ================= */

    open(index, { instant = false } = {}) {
      const items = this.galleryItems();
      const slides = document.createDocumentFragment();
      const thumbs = document.createDocumentFragment();
      const label = (template, n) => template.replace('{n}', n).replace('{total}', items.length);

      items.forEach((item, i) => {
        const img = item.querySelector('.product__media img');
        const src = img.currentSrc || img.src;
        const slide = document.createElement('div');
        slide.className = 'mlb__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', label(this.labels.slide, i + 1));
        // The gallery's copy is already cached: shown at once, the sharp one paints over it.
        slide.style.backgroundImage = `url("${src}")`;
        const big = document.createElement('img');
        big.className = 'mlb__img';
        big.alt = img.alt || '';
        big.draggable = false;
        big.decoding = 'async';
        big.srcset = SLIDE_WIDTHS.map((w) => `${sized(src, w)} ${w}w`).join(', ');
        big.sizes = '100vw';
        big.dataset.zoomSrc = sized(src, SHARP_WIDTH);
        if (img.naturalWidth) big.dataset.ratio = String(img.naturalWidth / img.naturalHeight);
        if (Math.abs(i - index) > 1) big.loading = 'lazy';
        // The placeholder only bridges the load: once the photo paints, it goes — otherwise a
        // photo moved or scaled below 1× would show a second copy of itself behind it.
        const dropPlaceholder = () => { slide.style.backgroundImage = 'none'; };
        if (big.complete && big.naturalWidth) dropPlaceholder();
        else big.addEventListener('load', dropPlaceholder, { once: true });
        slide.appendChild(big);
        slides.appendChild(slide);

        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'mlb__thumb';
        thumb.dataset.mlbThumb = String(i);
        thumb.setAttribute('aria-label', label(this.labels.thumb, i + 1));
        const thumbImg = document.createElement('img');
        thumbImg.src = sized(src, THUMB_WIDTH);
        thumbImg.alt = '';
        thumbImg.draggable = false;
        thumb.appendChild(thumbImg);
        thumbs.appendChild(thumb);
      });

      this.track.replaceChildren(slides);
      this.thumbs.replaceChildren(thumbs);
      this.thumbs.hidden = items.length < 2;

      const d = this.dialog;
      cancelClose(d);
      this.closeToken = null;
      d.querySelectorAll('.mlb__flyer').forEach((el) => el.remove());
      d.classList.remove('is-hero', 'is-closing-hero');
      d.classList.toggle('is-single', items.length < 2);
      d.classList.remove('is-immersive', 'is-zoomed', 'is-dragging');
      d.style.setProperty('--mlb-bg', '1');
      this.hint.hidden = false;
      if (!d.open) d.showModal();
      document.documentElement.classList.add('mlb-open');
      this.rtl = getComputedStyle(this.track).direction === 'rtl';
      this.z = { s: 1, x: 0, y: 0 };
      this.go(index, true);

      const source = items[index]?.querySelector('.product__media img');
      if (!instant && source) this.heroIn(source);
    }

    close() {
      if (!this.dialog.open) return;
      this.stopAnimation();
      this.setZoom({ s: 1, x: 0, y: 0 });
      const item = this.galleryItems()[this.index];
      // Leave the page gallery on the photo the shopper ended on.
      item?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
      const target = item?.querySelector('.product__media img');
      if (!target || !this.heroOut(target)) closeDialog(this.dialog, { ms: CLOSE_FALLBACK_MS });
    }

    onCancel(event) {
      event.preventDefault();
      this.close();
    }

    // Closed: release the slides (up to 2400px images) and the gesture state.
    onClosed() {
      document.documentElement.classList.remove('mlb-open');
      this.pointers.clear();
      this.unwatchPointers();
      this.stopAnimation();
      clearTimeout(this.singleTapTimer);
      this.gesture = null;
      this.lastTap = null;
      this.track.replaceChildren();
      this.thumbs.replaceChildren();
      this.dialog.querySelectorAll('.mlb__flyer').forEach((el) => el.remove());
      this.dialog.classList.remove('is-hero', 'is-closing-hero', 'is-zoomed', 'is-dragging');
    }

    onDialogClick(event) {
      if (event.target.closest('[data-mlb-close]')) return this.close();
      const step = event.target.closest('[data-mlb-step]');
      if (step) return this.go(this.index + Number(step.dataset.mlbStep));
      const thumb = event.target.closest('[data-mlb-thumb]');
      if (thumb) this.go(Number(thumb.dataset.mlbThumb));
    }

    onKeydown(event) {
      // RTL: the left arrow moves forward.
      const forward = this.rtl ? 'ArrowLeft' : 'ArrowRight';
      const back = this.rtl ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === forward) this.go(this.index + 1);
      else if (event.key === back) this.go(this.index - 1);
    }

    /* ================= hero (open from / close into the gallery) ================= */

    flyer(src, rect) {
      const el = document.createElement('img');
      el.className = 'mlb__flyer';
      el.src = src;
      el.alt = '';
      Object.assign(el.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
      this.dialog.appendChild(el);
      return el;
    }

    currentContentRect() {
      const img = this.currentImg();
      return contentRect(img.parentElement.getBoundingClientRect(), ratioOf(img));
    }

    async heroIn(source) {
      if (reduceMotion()) return;
      const from = contentRect(source.getBoundingClientRect(), ratioOf(source));
      const to = this.currentContentRect();
      if (!from.width || !to.width) return;
      const d = this.dialog;
      const fly = this.flyer(source.currentSrc || source.src, to);
      d.classList.add('is-hero');
      fly.style.transform = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`;
      d.style.setProperty('--mlb-bg', '0');
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      fly.style.transition = `transform ${HERO_MS}ms cubic-bezier(.2,.8,.2,1)`;
      fly.style.transform = 'none';
      d.style.setProperty('--mlb-bg', '1');
      await afterEvent(fly, 'transitionend', HERO_MS + 100);
      fly.remove();
      if (!d.classList.contains('is-closing-hero')) d.classList.remove('is-hero');
    }

    heroOut(target) {
      if (reduceMotion()) return false;
      const box = target.getBoundingClientRect();
      if (box.bottom < 0 || box.top > window.innerHeight || !box.width) return false;
      const to = contentRect(box, ratioOf(target));
      const from = this.currentContentRect();
      const img = this.currentImg();
      const d = this.dialog;
      const fly = this.flyer(img.currentSrc || img.src, from);
      d.classList.add('is-hero', 'is-closing-hero');
      if (this.dismissTransform) fly.style.transform = this.dismissTransform;
      this.dismissTransform = null;
      requestAnimationFrame(() => {
        fly.style.transition = `transform ${HERO_MS}ms cubic-bezier(.2,.8,.2,1)`;
        fly.style.transform = `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width})`;
        d.style.setProperty('--mlb-bg', '0');
      });
      // A reopen during the flight (open() clears the token) must not be closed by it.
      const token = (this.closeToken = {});
      afterEvent(fly, 'transitionend', HERO_MS + 100).then(() => {
        if (this.closeToken === token && d.open) d.close();
      });
      return true;
    }

    /* ================= navigation ================= */

    slides() {
      return [...this.track.children];
    }

    currentImg() {
      return this.track.children[this.index]?.querySelector('.mlb__img');
    }

    go(index, instant = false) {
      const slides = this.slides();
      if (!slides.length) return;
      this.stopAnimation();
      this.index = clamp(index, 0, slides.length - 1);
      slides.forEach((slide, i) => {
        if (i !== this.index) slide.firstElementChild.style.transform = '';
      });
      this.setZoom({ s: 1, x: 0, y: 0 });
      scrollToSlide(this.track, slides[this.index], instant);
      this.updateChrome();
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const index = Math.round(Math.abs(this.track.scrollLeft) / (this.track.clientWidth || 1));
        if (index !== this.index && this.z.s <= 1.01) {
          this.index = index;
          this.updateChrome();
        }
      });
    }

    updateChrome() {
      const total = this.track.children.length;
      this.count.textContent = `${this.index + 1} / ${total}`;
      [...this.thumbs.children].forEach((thumb, i) => {
        const active = i === this.index;
        thumb.classList.toggle('is-active', active);
        thumb.toggleAttribute('aria-current', active);
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      });
      this.dialog.querySelector('.mlb__nav--prev').disabled = this.index === 0;
      this.dialog.querySelector('.mlb__nav--next').disabled = this.index === total - 1;
    }

    /* ================= zoom model ================= */

    box() {
      const img = this.currentImg();
      return { w: img?.clientWidth || 1, h: img?.clientHeight || 1 };
    }

    // Allowed translation for a scale (origin at the top-left of the image box).
    bounds(s) {
      const { w, h } = this.box();
      if (s <= 1) return { minX: (w - w * s) / 2, maxX: (w - w * s) / 2, minY: (h - h * s) / 2, maxY: (h - h * s) / 2 };
      return { minX: w - w * s, maxX: 0, minY: h - h * s, maxY: 0 };
    }

    setZoom(z) {
      this.z = { ...z };
      const img = this.currentImg();
      if (!img) return;
      img.style.transform = z.s === 1 && !z.x && !z.y ? '' : `translate(${z.x}px, ${z.y}px) scale(${z.s})`;
      const zoomed = z.s > 1.01;
      this.dialog.classList.toggle('is-zoomed', zoomed);
      if (zoomed) this.sharpen(img);
    }

    // First zoom on a photo: load the 2400px file and swap it in once decoded.
    sharpen(img) {
      this.hint.hidden = true;
      if (!img.dataset.zoomSrc || img.dataset.sharp) return;
      img.dataset.sharp = '1';
      const sharp = new Image();
      sharp.src = img.dataset.zoomSrc;
      sharp
        .decode()
        .then(() => {
          if (!img.isConnected) return;
          img.removeAttribute('srcset');
          img.src = sharp.src;
        })
        .catch(() => {});
    }

    // Keep the point (px, py) of the image box under the same spot while scaling.
    zoomAround(s, px, py, from = this.z) {
      const cx = (px - from.x) / from.s;
      const cy = (py - from.y) / from.s;
      return { s, x: px - cx * s, y: py - cy * s };
    }

    // The nearest allowed state: scale within 1–MAX_SCALE, translation inside the edges.
    settle(z) {
      const s = z.s < 1 ? 1 : Math.min(z.s, MAX_SCALE);
      if (s === 1) return { s: 1, x: 0, y: 0 };
      const { w, h } = this.box();
      const scaled = s === z.s ? z : this.zoomAround(s, w / 2, h / 2, z);
      const b = this.bounds(s);
      return { s, x: clamp(scaled.x, b.minX, b.maxX), y: clamp(scaled.y, b.minY, b.maxY) };
    }

    animateTo(target, duration = ZOOM_MS) {
      this.stopAnimation();
      if (reduceMotion()) return this.setZoom(target);
      const from = { ...this.z };
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const k = easeOut(t);
        this.setZoom({ s: from.s + (target.s - from.s) * k, x: from.x + (target.x - from.x) * k, y: from.y + (target.y - from.y) * k });
        this.frame = t < 1 ? requestAnimationFrame(step) : null;
      };
      this.frame = requestAnimationFrame(step);
    }

    stopAnimation() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    // Double tap / click levels: fit → fill the screen → close-up.
    levels() {
      const { w, h } = this.box();
      const content = contentRect({ left: 0, top: 0, width: w, height: h }, ratioOf(this.currentImg()));
      let fill = Math.max(w / content.width, h / content.height);
      if (fill < 1.3) fill = 2;
      fill = Math.min(fill, MAX_SCALE - 0.8);
      return [1, fill, clamp(fill * 1.8, fill + 0.8, MAX_SCALE)];
    }

    cycleZoomAt(px, py) {
      const next = this.levels().find((level) => level > this.z.s + 0.05);
      this.animateTo(next ? this.settle(this.zoomAround(next, px, py)) : { s: 1, x: 0, y: 0 });
    }

    local(clientX, clientY) {
      const box = this.currentImg().parentElement.getBoundingClientRect();
      return { x: clientX - box.left, y: clientY - box.top };
    }

    /* ================= gestures ================= */

    // Moves and releases are followed on window while fingers are down: a pinch that
    // opened the viewer keeps delivering its events to the gallery photo it began on.
    watchPointers() {
      if (this.pointerWatch) return;
      this.pointerWatch = new AbortController();
      const signal = this.pointerWatch.signal;
      window.addEventListener('pointermove', (e) => this.onPointerMove(e), { passive: true, signal });
      window.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
      window.addEventListener('pointercancel', (e) => this.onPointerUp(e, true), { signal });
    }

    unwatchPointers() {
      this.pointerWatch?.abort();
      this.pointerWatch = null;
    }

    onPointerDown(event) {
      this.lastPointerType = event.pointerType;
      if (!event.target.classList.contains('mlb__img')) return;
      this.stopAnimation();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, t: performance.now() });
      this.watchPointers();
      if (this.pointers.size === 2) return this.startPinch();
      if (this.pointers.size > 1) return;
      this.gesture = {
        type: 'pending',
        x0: event.clientX,
        y0: event.clientY,
        z0: { ...this.z },
        samples: [{ x: event.clientX, y: event.clientY, t: performance.now() }],
        moved: false,
      };
      if (this.z.s > 1 || event.pointerType === 'mouse') event.target.setPointerCapture?.(event.pointerId);
    }

    startPinch() {
      const [a, b] = [...this.pointers.values()];
      this.gesture = {
        type: 'pinch',
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        z0: { ...this.z },
        mid0: this.local((a.x + b.x) / 2, (a.y + b.y) / 2),
        moved: true,
      };
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, t: performance.now() });
      const g = this.gesture;
      if (!g) return;

      if (g.type === 'pinch' && this.pointers.size >= 2) {
        const [a, b] = [...this.pointers.values()];
        const raw = g.z0.s * (Math.hypot(a.x - b.x, a.y - b.y) / g.dist);
        // Soft limits: past 4× the zoom stretches logarithmically, below 1× it resists.
        let s = raw;
        if (raw > MAX_SCALE) s = MAX_SCALE * (1 + Math.log(raw / MAX_SCALE) * OVERZOOM_DAMPING);
        if (raw < 1) s = Math.max(MIN_PINCH_SCALE, 1 - (1 - raw) * RUBBER);
        const mid = this.local((a.x + b.x) / 2, (a.y + b.y) / 2);
        const z = this.zoomAround(s, g.mid0.x, g.mid0.y, g.z0);
        this.setZoom({ s, x: z.x + (mid.x - g.mid0.x), y: z.y + (mid.y - g.mid0.y) });
        return;
      }

      const dx = event.clientX - g.x0;
      const dy = event.clientY - g.y0;
      g.samples.push({ x: event.clientX, y: event.clientY, t: performance.now() });
      if (g.samples.length > 6) g.samples.shift();
      if (Math.abs(dx) + Math.abs(dy) > MOVE_SLOP) g.moved = true;

      if (g.type === 'pending' && g.moved) {
        if (this.z.s > 1.01 && event.pointerType !== 'mouse') g.type = 'pan';
        else if (this.z.s <= 1.01 && event.pointerType === 'touch' && dy > 0 && Math.abs(dy) > Math.abs(dx) * 1.2) g.type = 'dismiss';
        else if (event.pointerType !== 'mouse') g.type = 'none'; // horizontal: the track scrolls natively
      }

      if (g.type === 'pan') this.panTo(g, dx, dy);
      else if (g.type === 'dismiss') this.dragToDismiss(dx, dy);
    }

    panTo(g, dx, dy) {
      const b = this.bounds(this.z.s);
      const rubber = (v, min, max) => (v > max ? max + (v - max) * RUBBER : v < min ? min + (v - min) * RUBBER : v);
      const rx = g.z0.x + dx;
      const ry = g.z0.y + dy;
      g.overX = rx > b.maxX ? rx - b.maxX : rx < b.minX ? rx - b.minX : 0;
      this.setZoom({ s: this.z.s, x: rubber(rx, b.minX, b.maxX), y: rubber(ry, b.minY, b.maxY) });
    }

    dragToDismiss(dx, dy) {
      const img = this.currentImg();
      const shrink = 1 - Math.min(dy / (this.box().h * 2.5), 0.25);
      img.style.transformOrigin = '50% 50%';
      img.style.transform = `translate(${dx * 0.4}px, ${dy}px) scale(${shrink})`;
      this.dialog.style.setProperty('--mlb-bg', String(1 - Math.min(dy / 320, 0.85)));
      this.dialog.classList.add('is-dragging');
    }

    velocity(g) {
      const s = g?.samples;
      if (!s || s.length < 2) return { vx: 0, vy: 0 };
      const a = s[0];
      const b = s[s.length - 1];
      const dt = Math.max(1, b.t - a.t);
      return { vx: (b.x - a.x) / dt, vy: (b.y - a.y) / dt };
    }

    onPointerUp(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      if (!this.pointers.size) this.unwatchPointers();
      const g = this.gesture;

      if (g?.type === 'pinch') {
        if (this.pointers.size === 1) {
          // One finger left: zoomed in, it pans on from here. At or below 1× there is nothing to
          // pan — spring back now (a pinch shrunk below 1× and released one finger at a time
          // stayed shrunk and shifted: "a second photo" over the placeholder).
          const [p] = [...this.pointers.values()];
          if (this.z.s > 1.01) {
            this.gesture = { type: 'pan', x0: p.x, y0: p.y, z0: { ...this.z }, samples: [{ ...p }], moved: true };
          } else {
            this.gesture = { type: 'none', x0: p.x, y0: p.y, z0: { s: 1, x: 0, y: 0 }, samples: [{ ...p }], moved: true };
            this.animateTo(this.settle(this.z), ZOOM_MS);
          }
          return;
        }
        this.gesture = null;
        this.animateTo(this.settle(this.z), ZOOM_MS);
        return;
      }
      if (this.pointers.size) return;
      this.gesture = null;
      if (!g) return;

      if (g.type === 'dismiss') {
        const dy = event.clientY - g.y0;
        if (!cancelled && (dy > DISMISS_DISTANCE || this.velocity(g).vy > DISMISS_VELOCITY)) this.dismiss();
        else this.springBack();
        return;
      }
      if (cancelled) return;

      if (g.type === 'pan') return this.endPan(g);
      if (g.moved || !event.target.classList?.contains('mlb__img')) return;

      const { x, y } = this.local(event.clientX, event.clientY);
      if (event.pointerType === 'mouse') return this.cycleZoomAt(x, y);
      this.onTap(x, y);
    }

    endPan(g) {
      // Zoomed and pulled well past the edge: the next / previous photo.
      if (Math.abs(g.overX || 0) > EDGE_FLIP_DISTANCE) {
        const towardNext = this.rtl ? g.overX > 0 : g.overX < 0;
        const target = this.index + (towardNext ? 1 : -1);
        if (target >= 0 && target < this.track.children.length) return this.go(target);
      }
      // Momentum: glide on in the direction of the flick, then settle inside the edges.
      const { vx, vy } = this.velocity(g);
      const fast = Math.abs(vx) + Math.abs(vy) > 0.3;
      this.animateTo(this.settle({ s: this.z.s, x: this.z.x + vx * MOMENTUM_MS, y: this.z.y + vy * MOMENTUM_MS }), fast ? ZOOM_MS * 1.6 : ZOOM_MS);
    }

    // Touch: a double tap zooms, a single tap shows / hides the controls.
    onTap(x, y) {
      const now = performance.now();
      clearTimeout(this.singleTapTimer);
      if (this.lastTap && now - this.lastTap.t < DOUBLE_TAP_MS && Math.hypot(x - this.lastTap.x, y - this.lastTap.y) < DOUBLE_TAP_SLOP) {
        this.lastTap = null;
        this.cycleZoomAt(x, y);
        return;
      }
      this.lastTap = { t: now, x, y };
      this.singleTapTimer = setTimeout(() => {
        this.lastTap = null;
        this.dialog.classList.toggle('is-immersive');
      }, DOUBLE_TAP_MS);
    }

    dismiss() {
      const img = this.currentImg();
      this.dismissTransform = img.style.transform;
      img.style.transform = '';
      img.style.transformOrigin = '';
      this.dialog.classList.remove('is-dragging');
      this.close();
    }

    springBack() {
      const img = this.currentImg();
      this.dialog.classList.remove('is-dragging');
      this.dialog.style.setProperty('--mlb-bg', '1');
      if (!img) return;
      img.style.transition = reduceMotion() ? 'none' : `transform ${ZOOM_MS}ms cubic-bezier(.2,.8,.2,1)`;
      img.style.transform = '';
      afterEvent(img, 'transitionend', ZOOM_MS + 50).then(() => {
        img.style.transition = '';
        img.style.transformOrigin = '';
      });
    }

    // Mouse, zoomed: the photo follows the cursor, so every corner is one move away.
    onMouseMove(event) {
      // Touch screens also fire a compatibility mousemove; only a real mouse pans this way.
      if (this.lastPointerType !== 'mouse' || this.z.s <= 1.01 || !event.target.classList.contains('mlb__img')) return;
      const { w, h } = this.box();
      const box = this.currentImg().parentElement.getBoundingClientRect();
      const fx = clamp((event.clientX - box.left) / box.width, 0, 1);
      const fy = clamp((event.clientY - box.top) / box.height, 0, 1);
      this.setZoom({ s: this.z.s, x: -(w * this.z.s - w) * fx, y: -(h * this.z.s - h) * fy });
    }

    onWheel(event) {
      if (!event.ctrlKey || !event.target.classList.contains('mlb__img')) return;
      event.preventDefault();
      const { x, y } = this.local(event.clientX, event.clientY);
      const s = clamp(this.z.s * (event.deltaY < 0 ? 1.15 : 1 / 1.15), 1, MAX_SCALE);
      this.setZoom(this.settle(this.zoomAround(s, x, y)));
    }
  }

  customElements.define('media-lightbox', MediaLightbox);
}
